<template>
  <div class="tw:!w-full tw:!flex tw:!justify-center tw:!items-center">
    <div
      class="search-widget-root tw:!relative tw:!w-full tw:!font-sans"
      :class="compact
        ? 'tw:!flex tw:!flex-row tw:!items-center tw:!gap-3 tw:!max-w-full'
        : 'tw:!flex tw:!flex-col tw:!max-w-lg'"
    >
      <Transition name="error-fade">
        <p
          v-if="hasError && !compact"
          class="error-message tw:!text-xs tw:!text-red-500 tw:!mb-2 tw:!text-center tw:!flex tw:!items-center tw:!justify-center tw:!gap-1 tw:!w-full"
        >
          {{ $t('search.validation.selectCountryError') }}
        </p>
      </Transition>

      <div class="search-input-wrapper tw:!relative" :class="compact ? 'tw:!flex-1' : 'tw:!w-full'">
        <div
          class="liquid-glass-input tw:!flex tw:!items-center tw:!gap-2 tw:!rounded-full tw:!transition-all tw:!duration-300"
          :class="[
            compact ? 'tw:!px-3 tw:!py-2' : 'tw:!px-4 tw:!py-3',
            isOpen ? 'input-focused' : '',
            hasError ? 'input-error' : ''
          ]"
        >
          <Search class="tw:!w-4 tw:!h-4 tw:!text-gray-400 tw:!flex-shrink-0" />
          <span class="tw:!w-px tw:!h-4 tw:!bg-gray-200 tw:!flex-shrink-0" />
          <input
            ref="inputRef"
            :value="query"
            type="text"
            :placeholder="isOpen ? '' : $t('search.placeholder')"
            class="tw:!flex-1 tw:!bg-transparent tw:!border-none tw:!outline-none tw:!text-gray-800 tw:!font-semibold tw:!placeholder-gray-800 tw:!caret-gray-800"
            :class="compact ? 'tw:!text-sm' : 'tw:!text-base'"
            @input="$emit('update:query', ($event.target as HTMLInputElement).value)"
            @focus="$emit('focus')"
            @blur="$emit('blur')"
            @keydown="$emit('keydown', $event)"
          />
          <button
            v-if="query"
            class="tw:!flex-shrink-0 tw:!text-gray-400 hover:tw:!text-gray-600 tw:!transition-colors"
            @mousedown.prevent="$emit('clear-query')"
          >
            <XCircle class="tw:!w-4 tw:!h-4" />
          </button>
        </div>

        <Transition name="dropdown">
          <div
            v-if="isOpen && results.length"
            class="liquid-glass-dropdown tw:!absolute tw:!top-full tw:!left-0 tw:!right-0 tw:!mt-2 tw:!rounded-2xl tw:!overflow-hidden tw:!z-[9999]"
          >
            <div class="tw:!flex tw:!items-center tw:!justify-between tw:!px-4 tw:!py-3 tw:!border-b tw:!border-white/10">
              <span class="tw:!text-xs tw:!font-bold tw:!tracking-widest tw:!text-gray-900 tw:!uppercase">
                {{ $t('search.destinations') }}
              </span>
              <span class="tw:!text-xs tw:!font-semibold tw:!text-gray-900/40 tw:!uppercase">
                {{ results.length }} {{ results.length !== 1 ? $t('search.resultsPlural') : $t('search.results') }}
              </span>
            </div>

            <ul>
              <li
                v-for="(item, index) in results"
                :key="item.slug"
                class="tw:!flex tw:!items-center tw:!justify-between tw:!px-4 tw:!py-3 tw:!cursor-pointer tw:!transition-all tw:!duration-150 tw:!border-b tw:!border-white/5 last:tw:!border-b-0"
                :class="autocompleteCounter === index ? 'list-item-active' : 'list-item-default'"
                @mousedown.prevent="$emit('select-item', item)"
              >
                <div class="tw:!flex tw:!items-center tw:!gap-3">
                  <div class="tw:!flex-shrink-0">
                    <img
                      v-if="item.type === 1"
                      :src="item.flag"
                      :alt="item.name"
                      class="tw:!block tw:!w-8 tw:!h-auto tw:!rounded-md tw:!border tw:!border-gray-200 tw:!object-contain"
                    />
                    <Globe v-else class="tw:!w-5 tw:!h-5 tw:!text-gray-800/70" />
                  </div>
                  <span class="tw:!text-sm tw:!font-medium tw:!text-gray-800 tw:!truncate tw:!max-w-[200px]">
                    {{ item.name }}
                  </span>
                </div>
                <span class="type-badge tw:!text-xs tw:!font-medium tw:!px-3 tw:!py-1 tw:!rounded-full tw:!flex-shrink-0">
                  {{ item.type === 1 ? $t('search.country') : $t('search.region') }}
                </span>
              </li>
            </ul>
          </div>
        </Transition>
      </div>

      <div
        class="search-tw:!flex-shrink-0"
        :class="compact ? '' : 'tw:!mt-3 tw:!w-full'"
      >
        <button
          class="od-cta-btn tw:!flex tw:!items-center tw:!justify-center tw:!rounded-full tw:!font-bold tw:!transition-all tw:!duration-200 tw:!whitespace-nowrap"
          :class="compact ? 'tw:!px-4 tw:!py-2 tw:!text-xs' : 'tw:!px-6 tw:!py-2 tw:!text-sm tw:!w-full'"
          @click="$emit('find-plan')"
        >
          {{ $t('search.esimPlan') }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { Search, XCircle, Globe } from 'lucide-vue-next'

type SearchItem = { name: string; slug: string; type: number; flag: string }

defineProps<{
  query: string
  isOpen: boolean
  hasError: boolean
  results: SearchItem[]
  autocompleteCounter: number
  selectedItem: SearchItem | null
  compact?: boolean
}>()

defineEmits<{
  'update:query': [value: string]
  'update:isOpen': [value: boolean]
  'select-item': [item: SearchItem]
  'find-plan': []
  'keydown': [event: KeyboardEvent]
  'clear-query': []
  'blur': []
  'focus': []
}>()

</script>

<style scoped>
.liquid-glass-input {
  background: rgba(255, 255, 255, 1);
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.03);
  transform: translateZ(0);
  will-change: box-shadow;
  isolation: isolate;
}

.liquid-glass-input.input-focused {
  border-color: rgba(0, 0, 0, 0.25);
  box-shadow:
    0 4px 32px rgba(0, 0, 0, 0.14),
    0 0 0 3px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.04);
}

.liquid-glass-input.input-error {
  border-color: rgba(239, 68, 68, 0.7);
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.10),
    0 0 0 3px rgba(239, 68, 68, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.03);
}

.liquid-glass-dropdown {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.20);
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.20), 0 2px 8px rgba(0, 0, 0, 0.10);
}

.list-item-default:hover { background: rgba(0, 0, 0, 0.05); }
.list-item-active { background: rgba(0, 0, 0, 0.08); }

.type-badge {
  background: rgba(0, 0, 0, 0.07);
  color: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(0, 0, 0, 0.10);
}

.od-cta-btn {
  background: var(--esim-primary);
    color: var(--esim-primary-text);
    border: 2px solid rgba(255, 255, 255, 0.3);
    box-shadow:
      0 2px 12px var(--esim-primary-glow),
      0 0 0 1px var(--esim-primary-glow);
    transition: all 0.2s ease-in-out;
    cursor: pointer;
}

.od-cta-btn:hover {
    background: var(--esim-primary-hover);
    box-shadow:
      0 4px 20px var(--esim-primary-glow-hover),
      0 0 0 2px var(--esim-primary-glow);
    transform: translateY(-1px);
  }
</style>